import React from "react";

import type { KonvaEventObject } from "konva/lib/Node";
import { Group, Rect, Label, Tag, Text } from "react-konva";

import { KONVA_GROUPS, KONVA_SHAPES } from "../constants";
import { useAnnotationStore } from "../store/annotationStore";
import type { EditorAnnotation } from "../store/annotationTypes";

interface BoxAnnotationProps {
  annotation: EditorAnnotation;
  screenPos: { x: number; y: number };
  screenWidth: number;
  screenHeight: number;
  isSelected: boolean;
  isAnnotating: boolean;
  isSelectionMode: boolean;
  dragBoundFunc: (pos: { x: number; y: number }) => { x: number; y: number };
  onSelect: () => void;
  onDragEnd: (e: KonvaEventObject<DragEvent>) => void;
  onTransform: (e: KonvaEventObject<Event>) => void;
  onTransformEnd: (e: KonvaEventObject<Event>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Renders a bounding box annotation with label and interactive controls
 */
export const BoxAnnotation: React.FC<BoxAnnotationProps> = ({
  annotation,
  screenPos,
  screenWidth,
  screenHeight,
  isSelected,
  isAnnotating,
  isSelectionMode,
  dragBoundFunc,
  onSelect,
  onDragEnd,
  onTransform,
  onTransformEnd,
  onMouseEnter,
  onMouseLeave,
}) => {
  /**
   * Component-level hover subscription prevents parent re-renders
   */
  const isHovered = useAnnotationStore((state) => state.hoveredAnnotationId === annotation.id);
  return (
    <Group
      dragBoundFunc={dragBoundFunc}
      draggable={isAnnotating && isSelectionMode}
      key={annotation.id}
      name={KONVA_GROUPS.BOX_ANNOTATION}
      onClick={onSelect}
      onDragEnd={onDragEnd}
    >
      <Rect
        className="box-rect"
        fill={isHovered ? "transparent" : "rgba(245, 131, 16, 0.26)"}
        height={screenHeight}
        id={annotation.id}
        name={KONVA_SHAPES.BOX_RECT}
        perfectDrawEnabled={false}
        stroke={isSelected && isSelectionMode ? "transparent" : "#F27D28"}
        strokeScaleEnabled={false}
        strokeWidth={isHovered ? 3.5 : 2}
        width={screenWidth}
        x={screenPos.x}
        y={screenPos.y}
        onMouseEnter={(e) => {
          if (isAnnotating) {
            const stage = e.target.getStage();
            if (stage) {
              stage.container().style.cursor = "pointer";
            }
          }
          onMouseEnter?.();
        }}
        onMouseLeave={(e) => {
          if (isAnnotating) {
            const stage = e.target.getStage();
            if (stage) {
              stage.container().style.cursor = "default";
            }
          }
          onMouseLeave?.();
        }}
        onTransform={onTransform}
        onTransformEnd={onTransformEnd}
      />
      <Label
        id={`label-${annotation.id}`}
        name={KONVA_SHAPES.BOX_LABEL}
        x={screenPos.x + screenWidth / 2}
        y={screenPos.y - 6}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Tag cornerRadius={3} fill="#F27D28" pointerDirection="down" pointerHeight={0} pointerWidth={0} />
        <Text align="center" fill="white" fontFamily="Inter" fontSize={14} padding={4} text={annotation.label} />
      </Label>
    </Group>
  );
};
